import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { format } from 'date-fns'
import PropTypes from 'prop-types'

import PageSectionHeader from '../../../../features/components/PageSectionHeader'
import CampaignsSummaryFilters from './CampaignsSummaryFilters'
import Can from '../../../../features/components/Can'
import ListItemsTotals from '../../../../features/components/ListItemsTotals'
import ListItemsTable from '../../../../features/components/ListItemsTable'
import LineItemFacebookEdit from '../../../../forms/Facebook/LineItemForms/LineItemFacebookEdit'
import LineItemGoogleEdit from '../../../../forms/Google/LineItemForms/LineItemGoogleEdit'
import LineItemTikTokEdit from '../../../../forms/Tiktok/LineItemForms/LineItemTikTokEdit'
import LineItemDV360Edit from '../../../../forms/DV360/LineItemForms/LineItemDv360Edit'
import LineItemFacebookDuplicate from '../../../../forms/Facebook/LineItemForms/LineItemFacebookDuplicate'
import AdDuplicationModal from '../../../../features/components/Modals/AdDuplicationModal'
import LineItemsChangeHistory from './LineItemsChangeHistory'

import { useShowAttributionWindows } from './useShowAttributionWindows'
import useGetLineItemDropdownOptions from './useGetLineItemDropdownOptions'
import { useGetListItemInsights } from '../../../../features/hooks/useGetListItemInsights'
import { createLineItemSummaryPageLink } from '../../helpers'
import { getSelectedPlatform } from '../../../../helpers/handlePlatformSelection'
import { useDeleteLineItemAction } from '../../../../features/hooks/useDeleteAction/useDeleteLineItemAction'

import { getLineItems, clearLineItems, clearUpdateLineItem } from '../../../../modules/actions/lineItems'
import {
  lineItemsSelector,
  lineItemsLoadingSelector,
  lineItemsTotalsSelector,
  lineItemsWasLoadedSelector,
  lineItemsErrorSelector,
  lineItemUpdateSelector,
  lineItemDeleteSelector
} from '../../../../modules/selectors/lineItems'
import { selectedAdAccountsSelector } from '../../../../modules/selectors/adAccounts'
import { campaignSelector, campaignWasLoadedSelector } from '../../../../modules/selectors/campaigns'
import { campaignsSummaryDateRangeSelector } from '../../../../modules/selectors/campaignsSummary'

import {
  DV_360_PLATFORM,
  FACEBOOK_PLATFORM,
  GOOGLE_PLATFORM,
  TIKTOK_PLATFORM
} from '../../../../constants/selectLists/platformList'
import { ROUTE_PARAMS } from '../../../../constants/routes'
import { DATES_FORMAT_BE } from '../../../../constants/dates'
import { LIST_ITEM_LINE_ITEM } from '../../../../constants/other'

function LineItemsList({ title = 'List of Ad Sets' }) {
  const dispatch = useDispatch()

  const { campaign_id } = useParams()

  const lineItems = useSelector(lineItemsSelector)
  const lineItemUpdate = useSelector(lineItemUpdateSelector)
  const { wasUpdated: lineItemWasUpdated, id: lineItemUpdateId, isLoading: lineItemUpdateIsLoading } = lineItemUpdate

  const lineItemDelete = useSelector(lineItemDeleteSelector)
  const { id: lineItemDeleteId, isLoading: lineItemDeleteIsLoading } = lineItemDelete

  // const lineItemUpdate = useSelector(lineItemUpdateSelector)
  // const lineItemDelete = useSelector(lineItemDeleteSelector)
  // const lineItemUpdateError = useSelector(lineItemUpdateErrorSelector)
  const selectedPlatform = getSelectedPlatform()
  const totalsItem = useSelector(lineItemsTotalsSelector)
  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)
  const { objective: campaignObjective, id: campaignId } = useSelector(campaignSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const campaignsSummaryDateRange = useSelector(campaignsSummaryDateRangeSelector)

  const insights = useGetListItemInsights(selectedPlatform)
  const shouldFetchAttributionWindows = useShowAttributionWindows(campaignObjective, selectedPlatform)

  const { date_preset, startDate, endDate } = campaignsSummaryDateRange
  const { id: selectedAdAccountId, currency_symbol: currencySymbol } = selectedAdAccount

  const summaryPageLinkExtraParams = useMemo(
    () => ({
      [ROUTE_PARAMS.campaignId]: campaignId
    }),
    [campaignId]
  )

  // useEffect(() => {
  //   return () => {
  //     dispatch(clearCampaignsSummarySelectedLineItems())
  //   }
  // }, [dispatch])

  const { handleDeleteRequest, DeleteLineItemModal } = useDeleteLineItemAction({
    updateAds: true
  })

  const getLineItemActionDropdownOptions = useGetLineItemDropdownOptions(handleDeleteRequest)

  const shouldProvideDropdownOptions = useMemo(
    () => getLineItemActionDropdownOptions({}).length,
    [getLineItemActionDropdownOptions]
  )

  const allowGetLineItems = useMemo(() => {
    switch (selectedPlatform) {
      case FACEBOOK_PLATFORM:
        // campaignObjective currently available for facebook
        return !!campaignObjective
      default:
        return true
    }
  }, [selectedPlatform, campaignObjective])

  const dynamicLineItemsPayload = useMemo(() => {
    switch (selectedPlatform) {
      case FACEBOOK_PLATFORM:
        // campaignObjective currently available for facebook
        return {
          campaign_objective: campaignObjective,
          ...(shouldFetchAttributionWindows && {
            action_attribution_windows: ['1d_view', '7d_view', '1d_click', '7d_click', '28d_click']
          })
        }
      default:
        return null
    }
  }, [selectedPlatform, campaignObjective, shouldFetchAttributionWindows])

  useEffect(() => {
    if (allowGetLineItems) {
      // clear data before getting new one, e.g. helps when we use date filtering
      dispatch(clearLineItems())

      dispatch(
        getLineItems(
          {
            account: selectedAdAccountId,
            campaign_id,
            ...(dynamicLineItemsPayload && dynamicLineItemsPayload),
            date_from: startDate && format(startDate, DATES_FORMAT_BE),
            date_to: endDate && format(endDate, DATES_FORMAT_BE),
            date_preset,
            insights
          },
          selectedPlatform
        )
      )
    }
  }, [
    dispatch,
    allowGetLineItems,
    dynamicLineItemsPayload,
    selectedAdAccountId,
    campaign_id,
    selectedPlatform,
    startDate,
    endDate,
    date_preset,
    insights,
    shouldFetchAttributionWindows
  ])

  useEffect(() => {
    if (lineItemWasUpdated) {
      dispatch(clearUpdateLineItem())
    }
  }, [dispatch, lineItemWasUpdated])

  return (
    <section id="line-items-table">
      <PageSectionHeader title={title} RightPanel={<CampaignsSummaryFilters />} />
      <ListItemsTable
        items={lineItems}
        isLoadingSelector={lineItemsLoadingSelector}
        errorSelector={lineItemsErrorSelector}
        wasLoadedSelector={lineItemsWasLoadedSelector}
        additionalDataIsLoading={!campaignWasLoaded}
        createItemLinkHelper={createLineItemSummaryPageLink}
        createItemLinkExtraParams={summaryPageLinkExtraParams}
        platform={selectedPlatform}
        currencySymbol={currencySymbol}
        getActionsDropdownOptions={shouldProvideDropdownOptions ? getLineItemActionDropdownOptions : null}
        itemUpdatingId={lineItemUpdateIsLoading ? lineItemUpdateId : ''}
        itemDeletingId={lineItemDeleteIsLoading ? lineItemDeleteId : ''}
        noDataText="This campaign is currently empty. To get started, please create a new ad set."
        listItemType={LIST_ITEM_LINE_ITEM}
        showGoal={selectedPlatform === FACEBOOK_PLATFORM}
      >
        {!!totalsItem && lineItems.length > 1 && (
          <ListItemsTotals
            items={lineItems}
            data={totalsItem}
            platform={selectedPlatform}
            currencySymbol={currencySymbol}
            hideDropdownColumn={!shouldProvideDropdownOptions}
            showGoal={selectedPlatform === FACEBOOK_PLATFORM}
          />
        )}
      </ListItemsTable>

      {/*edit forms*/}
      <Can I="update" a="LineItem">
        {selectedPlatform === FACEBOOK_PLATFORM && <LineItemFacebookEdit shouldBeCleared />}
        {selectedPlatform === GOOGLE_PLATFORM && <LineItemGoogleEdit shouldBeCleared />}
        {selectedPlatform === TIKTOK_PLATFORM && <LineItemTikTokEdit shouldBeCleared />}
        {selectedPlatform === DV_360_PLATFORM && <LineItemDV360Edit shouldBeCleared />}
      </Can>

      {/*duplicate forms*/}
      <Can I="create" a="LineItem">
        {selectedPlatform === FACEBOOK_PLATFORM && <LineItemFacebookDuplicate />}
      </Can>

      <LineItemsChangeHistory platform={selectedPlatform} />

      <AdDuplicationModal showRedirectionBtn />
      {DeleteLineItemModal}
    </section>
  )
}

LineItemsList.propTypes = {
  title: PropTypes.string
}

export default LineItemsList
